import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { DISABLED_FEATURES } from '../../config/api';
import { 
  User, 
  LogOut, 
  Settings, 
  BookOpen,
  Award,
  LayoutDashboard,
  Menu,
  X,
  ChevronDown,
  Heart,
  Beaker,
  Building2,
  GraduationCap,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import NotificationCenter from '../NotificationCenter';
import { useLearnerMode } from '../../hooks/useLearnerMode';

// Cerebro Logo - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

export const Header = ({ hideLogo = false }) => {
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { programmesOnly } = useLearnerMode();
  const learnerLocked = isAuthenticated && !isAdmin && programmesOnly;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Helper to check if feature is enabled (respects DISABLED_FEATURES override)
  const isFeatureEnabled = (feature) => {
    // Check code-level disable first (takes priority)
    if (DISABLED_FEATURES[feature]) return false;
    // Then check tenant settings
    return tenant.features?.[feature] !== false;
  };

  // Build nav links based on tenant feature flags
  const allNavLinks = [
    { 
      href: '/courses', 
      label: 'Courses', 
      feature: 'courses'
    },
    { href: '/external-learning', label: 'External', icon: Globe, feature: 'external_courses' },
    { href: '/labs', label: 'Learning Lab', icon: Beaker, feature: 'learning_labs' },
    { href: '/learning-pathways', label: 'Pathways', feature: 'learning_pathways' },
    { href: '/masterclasses', label: 'Masterclasses', feature: 'masterclasses' },
    { href: '/programmes', label: 'Programmes', feature: 'programmes' },
    { href: '/campaigns', label: 'Campaigns', feature: 'campaigns' },
    // { href: '/ebooks', label: 'eBooks', feature: 'ebooks' }, // Hidden - backend still functional
    { href: '/wsp', label: 'WSP', icon: Building2, feature: 'wsp' },
    { href: '/academy', label: 'Navigate Academy', icon: GraduationCap, feature: 'navigate_academy', highlight: true },
  ];

  // Authenticated-only nav links
  const authNavLinks = [
    { href: '/regulatory-alerts', label: 'Regulatory Alerts', feature: 'regulatory_alerts', highlight: true },
  ];
  
  // Programmes-only learner mode: Dashboard + Programmes only
  const learnerNavLinks = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/programmes', label: 'Programmes' },
  ];

  // Filter nav links based on feature flags
  const baseNavLinks = learnerLocked ? learnerNavLinks : allNavLinks.filter(link => 
    !link.feature || isFeatureEnabled(link.feature)
  );
  const navLinks = user?.role === 'instructor' ? [{ href: '/instructor', label: 'Instructor Hub', highlight: true }, ...baseNavLinks] : baseNavLinks;

  // Add authenticated links if user is logged in
  const authenticatedNavLinks = isAuthenticated && !learnerLocked
    ? authNavLinks.filter(link => !link.feature || isFeatureEnabled(link.feature))
    : [];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <header 
      className="sticky top-0 z-50 glass border-b border-zinc-800"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={learnerLocked ? '/dashboard' : '/'} 
            className="flex items-center gap-3 shrink-0"
            data-testid="header-logo"
          >
            {!hideLogo && (
              <>
                <img 
                  src={CEREBRO_LOGO} 
                  alt="Cerebro Core" 
                  className="h-10 sm:h-14 w-auto object-contain"
                />
                <div className="hidden sm:flex flex-col">
                  <span className="font-heading text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                    Cerebro Core
                  </span>
                  <span className="text-[10px] text-zinc-500 tracking-wider uppercase leading-tight">
                    A Workforce Capability Platform
                  </span>
                </div>
              </>
            )}
            {hideLogo && (
              <span className="font-heading text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                Cerebro Core
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 ml-12">
            {navLinks.map((link) => (
              link.submenu ? (
                // Dropdown menu for items with submenu
                <div key={link.href} className="relative group">
                  <button 
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive(link.href) || isActive('/external-learning')
                        ? 'text-navigate-gold' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-dropdown`}
                  >
                    {link.label}
                    <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-navigate-surface border border-zinc-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    {link.submenu.map((subItem) => (
                      <Link
                        key={subItem.href}
                        to={subItem.href}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          isActive(subItem.href) 
                            ? 'text-navigate-gold bg-zinc-800/50' 
                            : 'text-zinc-300 hover:text-white hover:bg-zinc-800/50'
                        }`}
                        data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {subItem.icon && <subItem.icon className="h-4 w-4" />}
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    link.highlight 
                      ? 'text-amber-400 hover:text-amber-300' 
                      : isActive(link.href) 
                        ? 'text-navigate-gold' 
                        : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              )
            ))}
            {/* Authenticated-only nav links */}
            {authenticatedNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  link.highlight 
                    ? 'text-amber-400 hover:text-amber-300' 
                    : isActive(link.href) 
                      ? 'text-navigate-gold' 
                      : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-4">
            {isAuthenticated ? (
              <>
                {/* Notifications - Real-time with WebSocket */}
                <NotificationCenter />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 focus:outline-none"
                      data-testid="user-menu-btn"
                    >
                      <Avatar className="h-8 w-8 border border-zinc-700">
                        <AvatarImage src={user?.picture} alt={user?.full_name} />
                        <AvatarFallback className="bg-navigate-surface text-navigate-gold text-xs">
                          {getInitials(user?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="h-4 w-4 text-zinc-400 hidden sm:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-56 bg-navigate-surface border-zinc-800"
                  >
                    <div className="px-3 py-2 border-b border-zinc-800">
                      <p className="text-sm font-medium text-white">{user?.full_name}</p>
                      <p className="text-xs text-zinc-400">{user?.email}</p>
                    </div>
                    <DropdownMenuItem 
                      onClick={() => navigate('/dashboard')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    {learnerLocked ? (
                      <DropdownMenuItem 
                        onClick={() => navigate('/programmes')}
                        className="cursor-pointer hover:bg-zinc-800"
                        data-testid="user-menu-programmes"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Programmes
                      </DropdownMenuItem>
                    ) : (
                    <>
                    <DropdownMenuItem 
                      onClick={() => navigate('/my-courses')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      My Courses
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/wishlist')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <Heart className="h-4 w-4 mr-2" />
                      Wishlist
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/certificates')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Certificates
                    </DropdownMenuItem>
                    </>
                    )}
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    {!learnerLocked && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/settings')}
                      className="cursor-pointer hover:bg-zinc-800"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-zinc-800" />
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className="cursor-pointer hover:bg-zinc-800"
                        >
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer hover:bg-zinc-800 text-red-400"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="btn-gold text-sm py-2 px-4"
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="p-2 text-zinc-400 hover:text-white md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-zinc-800 bg-navigate-dark">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              link.submenu ? (
                // Render submenu items inline for mobile
                <div key={link.href} className="space-y-1">
                  <span className="block px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                    {link.label}
                  </span>
                  {link.submenu.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2 pl-6 rounded-lg text-sm font-medium transition-colors ${
                        isActive(subItem.href) 
                          ? 'bg-navigate-surface text-navigate-gold' 
                          : 'text-zinc-400 hover:bg-navigate-surface hover:text-white'
                      }`}
                    >
                      {subItem.icon && <subItem.icon className="h-4 w-4" />}
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href) 
                      ? 'bg-navigate-surface text-navigate-gold' 
                      : 'text-zinc-400 hover:bg-navigate-surface hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            {/* Authenticated-only mobile nav links */}
            {authenticatedNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  link.highlight
                    ? 'text-amber-400 hover:bg-navigate-surface'
                    : isActive(link.href) 
                      ? 'bg-navigate-surface text-navigate-gold' 
                      : 'text-zinc-400 hover:bg-navigate-surface hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
